import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Users, Stethoscope, CalendarCheck, Calendar, Inbox, Activity } from "lucide-react";
import { getSummary } from "../../api/Dashboard/SummarySlice";

const TONES = {
  blue: { tile: "bg-[#EFF6FF] text-[#2563EB]", badge: "bg-[#EFF6FF] text-[#2563EB]" },
  teal: { tile: "bg-[#F0FDFA] text-[#0D9488]", badge: "bg-[#ECFDF5] text-[#059669]" },
  orange: { tile: "bg-[#FFF7ED] text-[#EA580C]", badge: "bg-[#FFF7ED] text-[#EA580C]" },
};

const StatCard = ({ icon: Icon, tone = "blue", title, value, badge, onClick, action }) => (
  <div
    className={`flex flex-col w-full h-full bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-3 shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    {/* Icon tile + tag (top right) */}
    <div className="flex items-start justify-between gap-2 mb-1.5">
      <div
        className={`flex items-center justify-center w-[38px] h-[38px] rounded-xl flex-shrink-0 ${TONES[tone].tile}`}
      >
        <Icon size={18} />
      </div>
      {badge && (
        <span
          className={`px-2 py-0.5 rounded-full text-[13px] font-semibold font-poppins whitespace-nowrap ${TONES[tone].badge}`}
        >
          {badge}
        </span>
      )}
    </div>
    <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">
      {typeof value === 'string' ? value : value.toLocaleString()}
    </div>
    {/* Title + action (bottom right) */}
    <div className="flex items-end justify-between gap-2 mt-0.5">
      <div className="text-[13px] uppercase tracking-wide font-medium text-[#64748B] font-poppins">
        {title}
      </div>
      {action && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
          className="btn-gradient flex gap-1 items-center font-poppins text-[12px] px-2.5 py-1 rounded-full whitespace-nowrap border-0 flex-shrink-0"
        >
          <span className="text-[14px] leading-none">+</span>
          {action.label}
        </button>
      )}
    </div>
  </div>
);

export default function OverviewStatsSection() {
  const dispatch = useDispatch();
  const { data: summaryData, loading, error } = useSelector((state) => state.GET_SUMMARY);

  const handleNavigation = (path) => {
    if (path) {
      window.location.href = path;
    }
  };

  const fetchData = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token && !loading) {
      dispatch(getSummary());
    }
  }, [dispatch, loading]);

  // Initial/periodic fetching is done once by the Dashboard (useDashboardData);
  // fetchData is only used by the Retry button below

  // Extract data from API response or use defaults
  const statsData = summaryData?.payload || {};

  // Show retry button if there's an error and no data
  const showRetry = error && !summaryData;

  return (
    <div className="flex flex-col gap-2 items-start justify-start relative w-full p-1">
      {/* Overview Title */}
      <div className="flex gap-3 items-center justify-between relative w-full">
        <h2 className="text-[#171717] text-base font-semibold font-poppins mb-0">
          Overview
        </h2>
        {showRetry && (
          <button
            onClick={fetchData}
            className="text-[#2563EB] text-xs font-semibold font-poppins bg-transparent border-0 hover:underline"
          >
            Retry
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="flex flex-col gap-2.5 items-start justify-start relative w-full">
        {/* First Row - 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 items-stretch justify-start relative w-full">
          <StatCard
            icon={Users}
            tone="blue"
            title="Total Members"
            value={loading ? "..." : (statsData.totalUsers || 0)}
            badge={loading ? null : `${statsData.activeUsers || 0} Active`}
            onClick={() => handleNavigation("/members")}
            action={{ label: "Add Member", onClick: () => handleNavigation("/add-member") }}
          />
          <StatCard
            icon={Stethoscope}
            tone="teal"
            title="Total Practitioners"
            value={loading ? "..." : (statsData.totalPractitioners || 0)}
            badge={loading ? null : `${statsData.activePractitioners || 0} Active`}
            onClick={() => handleNavigation("/practitioner")}
          />
          <StatCard
            icon={CalendarCheck}
            tone="orange"
            title="Total Appointments"
            value={loading ? "..." : (statsData.totalSessions || 0)}
            badge={loading ? null : `${statsData.completedSessions || 0} Completed`}
            onClick={() => handleNavigation("/appointments")}
          />
        </div>

        {/* Second Row - 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 items-stretch justify-start relative w-full">
          <StatCard
            icon={Calendar}
            tone="blue"
            title="Events"
            value={loading ? "..." : (statsData.totalEvent || 0)}
            badge={loading ? null : `${statsData.sheduledEvent || 0} Scheduled`}
            onClick={() => handleNavigation("/calender")}
            action={{ label: "Add Event", onClick: () => handleNavigation("/schedule-wellness") }}
          />
          <StatCard
            icon={Inbox}
            tone="teal"
            title="Requests"
            value={loading ? "..." : 30}
          />
          <StatCard
            icon={Activity}
            tone="orange"
            title="Engagement"
            value={loading ? "..." : (statsData.allEngagement || 0)}
          />
        </div>
      </div>
    </div>
  );
}